import { useCallback, useEffect, useRef } from 'react';

import type {
	AIUnifiedEventPayload,
	AiSuggestionsEntryPoint,
	AiSuggestionsEmptyStateType,
	AiSuggestionsEventPayload,
	AiSuggestionsRightRailEntryPoint,
} from '@atlaskit/editor-common/analytics';
import { AiSuggestionsEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';
import { fg } from '@confluence/feature-gating';

export const SUGGESTIONS_PANEL_ANALYTICS_SOURCE = 'suggestionsPanel';

export const SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT: AiSuggestionsEntryPoint =
	AiSuggestionsEntryPointValue.SUGGESTIONS_PANEL_EMPTY_STATE;

type SuggestionAnalyticsSnapshot = Pick<
	Suggestion,
	'action' | 'actionKind' | 'createdAt' | 'singleInstrumentationId' | 'toolCalls' | 'type'
>;

export type FireAiSuggestionsAnalyticsEvent = (
	event: AiSuggestionsEventPayload | AIUnifiedEventPayload,
) => void;

const getTimeSinceGenerationAttribute = (
	suggestion: Pick<Suggestion, 'createdAt'>,
): { timeSinceGenerationMs?: number } => {
	const createdAt = Date.parse(suggestion.createdAt);
	return Number.isFinite(createdAt)
		? { timeSinceGenerationMs: Math.max(0, Date.now() - createdAt) }
		: {};
};

const getSuggestionRemixAttributes = (
	suggestion: Pick<Suggestion, 'action' | 'actionKind'>,
): { remixSubtype?: string; remixType?: string } => {
	if (suggestion.actionKind !== 'SUGGESTED_REMIX') {
		return {};
	}
	return {
		...(suggestion.action?.launchOptions?.dynamicUiType
			? { remixType: suggestion.action.launchOptions.dynamicUiType }
			: {}),
		...(suggestion.action?.launchOptions?.dynamicUiSubtype
			? { remixSubtype: suggestion.action.launchOptions.dynamicUiSubtype }
			: {}),
	};
};

const getSuggestionTypeAttribute = (
	suggestion: Pick<Suggestion, 'type'>,
): { suggestionType?: string } =>
	fg('platform_editor_ai_suggestion_type_analytics') ? { suggestionType: suggestion.type } : {};

const getAffectedBlocks = (suggestion: SuggestionAnalyticsSnapshot): number => {
	const localIds = [
		...(suggestion.action?.localIds ?? []),
		...(suggestion.toolCalls?.flatMap((toolCall) =>
			toolCall.name === 'removeNodes'
				? (toolCall.arguments.localIds ?? [])
				: toolCall.arguments.localId
					? [toolCall.arguments.localId]
					: [],
		) ?? []),
	];

	return new Set(localIds).size;
};

export const useEmptyStateExposedAnalytics = ({
	emptyStateType,
	enabled,
	fireAnalyticsEvent,
}: {
	emptyStateType: AiSuggestionsEmptyStateType;
	enabled: boolean;
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent;
}): void => {
	const lastExposedEmptyStateRef = useRef<AiSuggestionsEmptyStateType | null>(null);

	useEffect(() => {
		if (!enabled) {
			lastExposedEmptyStateRef.current = null;
			return;
		}
		if (!fireAnalyticsEvent || lastExposedEmptyStateRef.current === emptyStateType) {
			return;
		}

		fireAnalyticsEvent({
			action: ACTION.EMPTY_STATE_EXPOSED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: { emptyStateType },
		});
		lastExposedEmptyStateRef.current = emptyStateType;
	}, [emptyStateType, enabled, fireAnalyticsEvent]);
};

export const useReasoningViewedAnalytics = (
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent,
): ((suggestion: Suggestion) => void) => {
	return useCallback(
		(suggestion: Suggestion) => {
			fireAnalyticsEvent?.({
				action: ACTION.REASONING_VIEWED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					singleInstrumentationId: suggestion.singleInstrumentationId,
					actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
					affectedBlocks: getAffectedBlocks(suggestion),
					agentId: suggestion.createdByAgent?.accountId,
					hasSources: Boolean(suggestion.sources?.length),
					interactionPoint: 'sidebar',
					reasoningCharacterCount: suggestion.reasoning?.length ?? 0,
					...getTimeSinceGenerationAttribute(suggestion),
					...getSuggestionRemixAttributes(suggestion),
					suggestionId: suggestion.id,
					...getSuggestionTypeAttribute(suggestion),
				},
			});
			// Mirrors the editor plugin's unified `aiResult actioned` payload for expanding details.
			if (suggestion.singleInstrumentationId) {
				const agentId = suggestion.createdByAgent?.accountId;
				fireAnalyticsEvent?.({
					action: ACTION.ACTIONED,
					actionSubject: ACTION_SUBJECT.AI_RESULT,
					actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
					eventType: EVENT_TYPE.TRACK,
					attributes: {
						aiFeatureName: 'Editor AI',
						aiExperienceName: 'Suggested edits',
						singleInstrumentationID: suggestion.singleInstrumentationId,
						isAIFeature: 1,
						userGeneratedAI: 1,
						proactiveAIGenerated: 0,
						invokedFrom: 'sidebar',
						...(agentId ? { agentId } : {}),
						aiGroupId: undefined,
						aiResultAction: 'detailsExpanded',
					},
				});
			}
		},
		[fireAnalyticsEvent],
	);
};

export const useCancelledAnalytics = (
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent,
): (() => void) => {
	return useCallback(() => {
		fireAnalyticsEvent?.({
			action: ACTION.CANCELLED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: { interactionPoint: 'sidebar' },
		});
	}, [fireAnalyticsEvent]);
};

export const useSuggestionsPanelOpenAnalytics = ({
	entryPoint,
	fireAnalyticsEvent,
	isFullPanelLoading,
	suggestions,
}: {
	entryPoint: AiSuggestionsRightRailEntryPoint;
	fireAnalyticsEvent?: FireAiSuggestionsAnalyticsEvent;
	isFullPanelLoading: boolean;
	suggestions?: readonly SuggestionAnalyticsSnapshot[];
}): void => {
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current || isFullPanelLoading || !fireAnalyticsEvent) {
			return;
		}

		const suggestionDetails = (suggestions ?? []).map((suggestion) => ({
			singleInstrumentationId: suggestion.singleInstrumentationId,
			...getSuggestionTypeAttribute(suggestion),
			actionKind: suggestion.actionKind ?? 'SUGGESTED_EDIT',
			affectedBlocks: getAffectedBlocks(suggestion),
			...getTimeSinceGenerationAttribute(suggestion),
			...getSuggestionRemixAttributes(suggestion),
		}));
		fireAnalyticsEvent({
			action: ACTION.RIGHT_RAIL_VIEWED,
			actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
			eventType: EVENT_TYPE.TRACK,
			attributes: {
				entryPoint,
				numberOfSuggestions: suggestionDetails.length,
				suggestionDetails,
			},
		});
		hasFiredRef.current = true;
	}, [entryPoint, fireAnalyticsEvent, isFullPanelLoading, suggestions]);
};
